/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import logoArabic from "../../../assets/logoArabic.png";
import logoEnglish from "../../../assets/logoEnglish.png";
import LegalDocumentModal from "../../legal/LegalDocumentModal";
import { LEGAL_DOCUMENTS, loadLegalDocument } from "../../../utils/legalDocuments";
import { NAV_LINKS } from "./landing.data";

const THEME = {
  darkPurple: "#112758",
  purple: "#0E3981",
  gold: "#BBDFF9",
  textLight: "#f4f9ff",
};

export default function LandingFooter({ isArabic }) {
  const { t } = useTranslation();
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalTitle, setLegalTitle] = useState("");
  const [legalContent, setLegalContent] = useState("");
  const [legalLoading, setLegalLoading] = useState(false);

  const scrollTo = (id) => {
    const target =
      id === "support"
        ? document.getElementById("support-email")
        : document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  const openLegalDoc = async (docKey) => {
    const doc = LEGAL_DOCUMENTS[docKey];
    if (!doc) return;

    setLegalTitle(isArabic ? doc.titleAr : doc.titleEn);
    setLegalContent("");
    setLegalLoading(true);
    setLegalModalOpen(true);

    try {
      const text = await loadLegalDocument(docKey, isArabic ? "ar" : "en");
      setLegalContent(text);
    } catch {
      setLegalContent(
        isArabic
          ? "تعذر تحميل المستند حالياً."
          : "Unable to load the document right now."
      );
    } finally {
      setLegalLoading(false);
    }
  };

  return (
    <footer className="gl-footer">
      <div className="gl-shell">
        <div className="gl-footer__grid">
          <div>
            <img
              className="gl-footer__logo"
              src={isArabic ? logoArabic : logoEnglish}
              alt="مدار المجرة"
            />
            <p className="gl-footer__desc">{t("footer.description")}</p>
          </div>

          <div className="gl-footer__col">
            <h4>{t("footer.linksTitle")}</h4>
            <ul>
              {NAV_LINKS.map((link) => (
                <li key={link.id}>
                  <button type="button" onClick={() => scrollTo(link.id)}>
                    {isArabic ? link.labelAr : link.labelEn}
                  </button>
                </li>
              ))}
              <li>
                <button type="button" onClick={() => scrollTo("support")}>
                  {t("footer.support")}
                </button>
              </li>
            </ul>
          </div>

          <div className="gl-footer__col gl-footer__contact">
            <h4>{t("footer.contactTitle")}</h4>
            <p>{t("footer.location")}</p>
            <p id="support-email">
              <a href="mailto:support@madar.com">{t("footer.email")}</a>
            </p>
          </div>
        </div>

        <div className="gl-footer__bottom">
          <p className="gl-footer__copy">
            © {new Date().getFullYear()}{" "}
            <span>
              {isArabic
                ? "مدار المجرة للتجارة العامة والتجارة الإلكترونية وخدمات التوصيل"
                : "Madar Al-Majarra for General Trading, E-Commerce, and Delivery Services"}
            </span>{" "}
            — {t("footer.rights")}
          </p>

          <div className="gl-footer__legal">
            <button type="button" onClick={() => openLegalDoc("terms")}>
              {isArabic ? "الشروط والأحكام" : "Terms and Conditions"}
            </button>
            <span>|</span>
            <button type="button" onClick={() => openLegalDoc("privacy")}>
              {isArabic ? "سياسة الخصوصية" : "Privacy Policy"}
            </button>
          </div>
        </div>
      </div>

      <LegalDocumentModal
        isOpen={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        title={legalTitle}
        content={legalContent}
        loading={legalLoading}
        isArabic={isArabic}
        themeColors={THEME}
      />
    </footer>
  );
}
